import { createContext } from "react";
import { Auth } from "../types/models/Auth";
import { useLocalStorage } from "usehooks-ts";
import axios from "../lib/axios";
import { AxiosResponse } from "axios";
import { Roles } from "../types/enums/Roles";
import { getRolesByToken } from "../utils/auth";
import { useOrgApp } from "../hooks/context/useOrgApp";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { toastOptions } from "../data/toastOptions";

interface AuthContextProps {
  auth: Auth;
  setAuth: (value: React.SetStateAction<Auth>) => void;
  login: (user: string, pwd: string, code?: string) => Promise<number>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = (props: { children: React.ReactNode }) => {
  const [auth, setAuth] = useLocalStorage<Auth>("auth", { isAuthenticated: false });
  const { setOrgApp } = useOrgApp();
  const queryClient = useQueryClient();

  const login = async (username: string, password: string, code?: string): Promise<number> => {
    try {
      const response: AxiosResponse = await axios.post("/login_check", JSON.stringify({ username, password, code }), {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      // Filter By User Role
      if (!getRolesByToken(response.data.token).includes(Roles.CUSTOMER)) return 401;

      // save authentication credentials
      setAuth((prev) => ({
        ...prev,
        accessToken: response.data.token,
        isAuthenticated: true,
        isExpiredSubscription: false,
      }));

      return response?.status;
    } catch (err: any) {
      if (!err?.response) {
        return -1;
      }
      // Expired Subscription case
      if (err.response?.status === 403) {
        setAuth({ isAuthenticated: true, isExpiredSubscription: true });
      }
      return err.response?.status;
    }
  };

  const logout = async () => {
    // call logout api to remove refresh token
    try {
      await axios.get("/token/invalidate");
    } catch (err: any) {
      //response 400 means that there is no refresh token to be deleted
      if (err.response.status !== 400) {
        toast.error("Can't logout, please try agan later", toastOptions);
        return;
      }
    }
    // Remove the cache for all queries
    queryClient.removeQueries();
    setOrgApp({});
    setAuth({ isAuthenticated: false });
  };

  return <AuthContext.Provider value={{ auth, setAuth, login, logout }}>{props.children}</AuthContext.Provider>;
};
